import {
  createSelectorHooks,
  type ZustandHookSelectors,
} from 'auto-zustand-selectors-hook';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import {
  type MaterialBrightnessFilter,
  type MaterialFilterOption,
} from '$/services/usecases/materials';
import { type MaterialType } from '$/services/usecases/materials/mapper/materials';
import {
  mapCollectionFilterToTrackingAction,
  trackCollectionEvent,
} from '$/stores/collectionTracking';

export type CollectionViewOptions = 'grid' | 'fan' | 'list';

export type State = {
  activeType: MaterialType | undefined;
  appliedFilterOptions: MaterialFilterOption[];
  brightnessFilter: MaterialBrightnessFilter;
  selectedViewMode: CollectionViewOptions;
};

type Actions = {
  isFilterActive: (option: string, value: string | number) => boolean;
  isAnyCategoryActive: (options: string[]) => boolean;
  setBrightnessFilter: (filter: MaterialBrightnessFilter) => void;
  getActiveInCategory: (
    type: string | undefined,
  ) => MaterialFilterOption | undefined;
  resetAppliedFilter: () => void;
  resetAppliedFilterOption: (options: string[]) => void;
  toggleAppliedFilterOption: (
    filter: { option: string; value: string | number },
    opt?: { isMultiFilter?: boolean },
  ) => void;
  switchType: (type: MaterialType | undefined) => void;
};

export const initial: State = {
  appliedFilterOptions: [],
  brightnessFilter: [0, 100],
  activeType: undefined,
  selectedViewMode: 'grid',
};

export const collectionStore = create<State & Actions>()(
  subscribeWithSelector((set, get) => ({
    ...initial,

    setBrightnessFilter: ([from, to]) => {
      trackCollectionEvent(
        'FilterCollections',
        'LightReferenceValueMin',
        from.toString(),
      );
      trackCollectionEvent(
        'FilterCollections',
        'LightReferenceValueMax',
        to.toString(),
      );
      set({ brightnessFilter: [from, to] });
    },

    switchType: (type) => {
      set({
        activeType: type,
        appliedFilterOptions: [],
        brightnessFilter: initial.brightnessFilter,
      });
    },

    getActiveInCategory: (option) => {
      if (!option) return undefined;
      return get().appliedFilterOptions.find((opt) => opt.option === option);
    },

    isFilterActive: (option, value) => {
      return get().appliedFilterOptions.some((applied) => {
        if (Array.isArray(applied.value)) {
          return applied.option === option && applied.value.includes(value);
        }

        return applied.option === option && applied.value === value;
      });
    },

    isAnyCategoryActive: (options) => {
      return options.some((option) =>
        get().appliedFilterOptions.some((opt) => opt.option === option),
      );
    },

    resetAppliedFilter: () => {
      set({
        appliedFilterOptions: initial.appliedFilterOptions,
        brightnessFilter: initial.brightnessFilter,
      });
    },

    resetAppliedFilterOption: (options) => {
      if (options.includes('brightness')) {
        return get().setBrightnessFilter(initial.brightnessFilter);
      }

      const newFilterGroups = get().appliedFilterOptions.filter(
        (filter: MaterialFilterOption) => !options.includes(filter.option),
      );

      set({ appliedFilterOptions: newFilterGroups });
    },

    toggleAppliedFilterOption: (filter, opt) => {
      const state = get();

      const action = mapCollectionFilterToTrackingAction(
        filter.option,
        state.activeType,
      );
      if (!action) {
        console.error(
          new Error(`Could not map tracking action for: ${filter.option}`),
        );
      } else {
        trackCollectionEvent('FilterCollections', action, String(filter.value));
      }

      const filterWithoutExisting = state.appliedFilterOptions.filter(
        (f) => f.option !== filter.option,
      );

      if (opt?.isMultiFilter) {
        const existingFilter = state.appliedFilterOptions.find(
          (f) => f.option === filter.option,
        );

        if (existingFilter && Array.isArray(existingFilter.value)) {
          const updatedValue = existingFilter.value.includes(filter.value)
            ? existingFilter.value.filter((v) => v !== filter.value)
            : [...existingFilter.value, filter.value];

          return set({
            appliedFilterOptions: [
              ...filterWithoutExisting,
              { option: filter.option, value: updatedValue },
            ],
          });
        }

        return set({
          appliedFilterOptions: [
            ...filterWithoutExisting,
            { option: filter.option, value: [filter.value] },
          ],
        });
      }

      const existingFilter = state.appliedFilterOptions.find(
        (f) => f.option === filter.option && f.value === filter.value,
      );

      if (existingFilter) {
        return set({ appliedFilterOptions: filterWithoutExisting });
      }

      return set({ appliedFilterOptions: [...filterWithoutExisting, filter] });
    },
  })),
);

export const getCollectionStore = () => collectionStore.getState();

export const useCollectionStore = createSelectorHooks(
  collectionStore,
) as typeof collectionStore & ZustandHookSelectors<State & Actions>;
